import { uploadToS3 } from "../S3/utils/S3.utils";
import { TIMESTAMP_FORMAT } from "../DateTime/DateTime.constants";
import { video_format_keys } from "../File/File.constants";
import { extractVideoThumbnail } from "../File/utils/videoThumbnail.utils";
import {
  ATTACHMENT_UPLOAD_BUCKET,
  CONTENT_UPLOAD_PUBLIC_BUCKET,
} from "./MediaUpload.constants";
import { format } from "date-fns";
import { getUserID } from "utils/AuthUtil";
import { logError } from "utils/error";

export const isVideo = (fileType) => {
  if (!fileType) return false;
  return Object.values(video_format_keys).includes(
    fileType.replace("video/", "")
  );
};

/**
 * Generates a key for uploading files based on the specified parameters.
 *
 * @param {Object} params - The input parameters for generating the upload key.
 * @param {string} params.sectionName - The section or folder name where the file will be stored.
 * @param {string} params.fileName - The name of the file being uploaded.
 * @param {boolean} params.isPublic - A flag indicating whether the file is to uploaded in public or private bucket.
 * @returns {string} - The generated upload key for the file.
 *
 */
export const getUploadKey = ({ sectionName, fileName, isPublic }) => {
  return `${
    isPublic ? "exly-content-upload" : "exly-attachments"
  }/${getUserID()}/${sectionName}/${format(
    new Date(),
    TIMESTAMP_FORMAT
  )}/${fileName}`;
};

/**
 * Uploads a media file to an S3 bucket (public or private) and returns the file's URL, thumbnail URL (if applicable), and file name.
 *
 * @param {Object} params - The input parameters for uploading the media file.
 * @param {File} params.file - The media file to be uploaded.
 * @param {string} params.sectionName - The name of the section where the file will be stored.
 * @param {boolean} [params.shouldExtractThumbnail=false] - Indicates whether a thumbnail should be extracted for video files.
 * @param {boolean} [params.isPublic=true] - Indicates whether the file should be uploaded to a public or private S3 bucket.
 *
 * @returns {Promise<Object>} A promise resolving to an object containing:
 *  - {string} fileUrl: The URL of the uploaded file.
 *  - {string|null} thumbnailFileUrl: The URL of the thumbnail (only for video files).
 *  - {string} fileName: The name of the uploaded file.
 */
export const uploadMedia = async ({
  file,
  sectionName,
  shouldExtractThumbnail = false,
  isPublic = true,
}) => {
  if (!file) return;
  try {
    let uploadedFile;
    let thumbnailFile;

    const thumbnail =
      shouldExtractThumbnail ?? isVideo(file?.type)
        ? await extractVideoThumbnail(file, `${file.name}_thumbnail.jpg`)
        : null;

    const key = getUploadKey({
      sectionName,
      fileName: file?.name,
      isPublic,
    });
    uploadedFile = await uploadToS3({
      file: file,
      key: key,
      bucket: isPublic
        ? CONTENT_UPLOAD_PUBLIC_BUCKET
        : ATTACHMENT_UPLOAD_BUCKET,
      contentType: file.type,
    });

    if (thumbnail) {
      const thumbnailKey = getUploadKey({
        sectionName,
        fileName: `thumbnail_${file?.name}`,
        isPublic,
      });
      thumbnailFile = await uploadToS3({
        file: thumbnail,
        key: thumbnailKey,
        bucket: isPublic
          ? CONTENT_UPLOAD_PUBLIC_BUCKET
          : ATTACHMENT_UPLOAD_BUCKET,
        contentType: thumbnail.type,
      });
    }

    return {
      fileUrl: uploadedFile?.Location,
      thumbnailFileUrl: thumbnailFile?.Location,
      fileName: file?.name,
    };
  } catch (error) {
    logError({
      error,
      when: "uploadMedia",
      occuredAt: "features/Common/modules/MediaUpload/MediaUpload.utils.js",
    });
  }
};
